import React, { useEffect, useMemo, useRef, useState } from "react";
import PopularRoutes from "../helpers/PopularRoutes";
import PopularBrandsPage from "../helpers/PopularBrandsPage";
import Hero from "../../assets/hero.jpg";
import {
  MapPin,
  Calendar,
  Users,
  Search,
  Train,
  ChevronDown,
  X,
  Plus,
  Minus,
  ShieldCheck,
  Clock,
  Map,
  ArrowRightLeft,
  CheckCircle2,
  Award,
} from "lucide-react";

// --- Data & Mocks ---

const popularRoutes = [
  {
    from: "New York",
    to: "Washington DC",
    time: "3h 20m",
    fare: "from $31",
    image:
      "https://images.unsplash.com/photo-1617581629397-a72507c3de9e?auto=format&fit=crop&q=80&w=800&h=400",
  },
  {
    from: "Boston",
    to: "New York",
    time: "4h 05m",
    fare: "from $28",
    image:
      "https://images.unsplash.com/photo-1506501139174-099022df5260?auto=format&fit=crop&q=80&w=800&h=400",
  },
  {
    from: "Chicago",
    to: "Milwaukee",
    time: "1h 29m",
    fare: "from $19",
    image:
      "https://images.unsplash.com/photo-1494522855154-9297ac14b55f?auto=format&fit=crop&q=80&w=800&h=400",
  },
];

const features = [
  {
    icon: ShieldCheck,
    title: "Secure & Encrypted",
    text: "Bank-level security for your payments and instant mobile ticket delivery.",
  },
  {
    icon: Clock,
    title: "24/7 Concierge Support",
    text: "Priority assistance for schedule changes, upgrades, and last-minute plans.",
  },
  {
    icon: Map,
    title: "Intelligent Routing",
    text: "Advanced algorithms compare stations and schedules to find the optimal journey.",
  },
];

const partnerBrands = ["Amtrak", "Deutsche Bahn", "Renfe", "Italo", "SNCF"];

const railStations = [
  {
    city: "New York",
    station: "NYP",
    code: "NYP",
    platform: "A2",
    zone: "Empire",
  },
  {
    city: "Washington DC",
    station: "Union Station",
    code: "WAS",
    platform: "D6",
    zone: "Capitol",
  },
  {
    city: "Boston",
    station: "South Station",
    code: "BOS",
    platform: "B4",
    zone: "Harbor",
  },
  {
    city: "Chicago",
    station: "Union Station",
    code: "CHI",
    platform: "C9",
    zone: "Lake Shore",
  },
  {
    city: "Los Angeles",
    station: "Union Station",
    code: "LAX",
    platform: "G3",
    zone: "Pacific",
  },
  {
    city: "Seattle",
    station: "King Street Station",
    code: "SEA",
    platform: "K1",
    zone: "Cascade",
  },
  {
    city: "Miami",
    station: "Central Station",
    code: "MIA",
    platform: "M5",
    zone: "Sun Rail",
  },
  {
    city: "San Francisco",
    station: "Emeryville Connector",
    code: "EMY",
    platform: "P7",
    zone: "Bay Link",
  },
];

const travelClasses = [
  "Economy",
  "Premium Economy",
  "Business Class",
  "First Class",
  "Private Sleeper",
];

const stationRepository = {
  async search(query) {
    const normalizedQuery = query.toLowerCase().trim();
    await new Promise((resolve) => setTimeout(resolve, 300));
    if (!normalizedQuery) return railStations.slice(0, 4);
    return railStations
      .filter((place) =>
        [place.city, place.station, place.code, place.zone]
          .join(" ")
          .toLowerCase()
          .includes(normalizedQuery),
      )
      .slice(0, 6);
  },
};

const stationLabel = (station) =>
  station ? `${station.city} (${station.code})` : "";

const todayValue = () => new Date().toISOString().split("T")[0];
const tomorrowValue = () => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return date.toISOString().split("T")[0];
};

const formatShortDate = (dateString) => {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("-");
  return `${month}/${day}/${year}`;
};

// --- Components ---

function FieldError({ children }) {
  if (!children) return null;
  return (
    <p className="absolute -bottom-4 left-2 text-[10px] sm:text-[11px] font-medium text-rose-500">
      {children}
    </p>
  );
}

export default function Home() {
  const widgetRef = useRef(null);
  const passengerRef = useRef(null);
  const defaultDate = useMemo(() => tomorrowValue(), []);
  const minDate = useMemo(() => todayValue(), []);

  const [search, setSearch] = useState({
    from: railStations.find((s) => s.code === "NYP"),
    to: null,
    date: defaultDate,
    returnDate: "",
    adults: 1,
    children: 0,
    tripType: "one-way",
    travelClass: travelClasses[1],
  });

  const [draft, setDraft] = useState({
    from: "New York (NYP)",
    to: "",
  });

  const [activeField, setActiveField] = useState(null);
  const [stationResults, setStationResults] = useState(
    railStations.slice(0, 4),
  );
  const [stationLoading, setStationLoading] = useState(false);
  const [showPassengerPanel, setShowPassengerPanel] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSearching, setIsSearching] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const passengerTotal = search.adults + search.children;

  useEffect(() => {
    if (!activeField || !draft[activeField].trim()) {
      setStationResults(railStations.slice(0, 4));
      setStationLoading(false);
      return;
    }
    let cancelled = false;
    setStationLoading(true);
    const timer = setTimeout(() => {
      stationRepository.search(draft[activeField]).then((results) => {
        if (!cancelled) {
          setStationResults(results);
          setStationLoading(false);
        }
      });
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [activeField, draft]);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (widgetRef.current && !widgetRef.current.contains(event.target))
        setActiveField(null);
      if (passengerRef.current && !passengerRef.current.contains(event.target))
        setShowPassengerPanel(false);
    };
    document.addEventListener("pointerdown", closeOnOutsideClick);
    return () =>
      document.removeEventListener("pointerdown", closeOnOutsideClick);
  }, []);

  const handleSwap = () => {
    setSearch((prev) => ({ ...prev, from: prev.to, to: prev.from }));
    setDraft((prev) => ({ ...prev, from: prev.to, to: prev.from }));
  };

  const updatePassengerGroup = (group, step) => {
    setSearch((current) => ({
      ...current,
      [group]: Math.min(
        group === "adults" ? 8 : 6,
        Math.max(group === "adults" ? 1 : 0, current[group] + step),
      ),
    }));
  };

  const selectStation = (field, station) => {
    setSearch((current) => ({ ...current, [field]: station }));
    setDraft((current) => ({ ...current, [field]: stationLabel(station) }));
    setActiveField(null);
    setErrors((current) => ({ ...current, [field]: "" }));
  };

  const clearStation = (field) => {
    setSearch((current) => ({ ...current, [field]: null }));
    setDraft((current) => ({ ...current, [field]: "" }));
    setActiveField(field);
  };

  const validateSearch = () => {
    const nextErrors = {};
    if (!search.from) nextErrors.from = "Select departure.";
    if (!search.to) nextErrors.to = "Select destination.";
    if (search.from?.code && search.from?.code === search.to?.code)
      nextErrors.to = "Must be different.";
    if (!search.date) nextErrors.date = "Select date.";
    if (search.tripType === "round-trip" && !search.returnDate)
      nextErrors.returnDate = "Select return.";
    if (search.tripType === "round-trip" && search.returnDate < search.date)
      nextErrors.returnDate = "Invalid date.";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validateSearch()) return;

    setIsSearching(true);
    setConfirmation(null);

    setTimeout(() => {
      setIsSearching(false);
      setConfirmation({
        route: `${search.from.city} to ${search.to.city}`,
        details: `${formatShortDate(search.date)} • ${passengerTotal} Guest${passengerTotal > 1 ? "s" : ""} • ${search.travelClass}`,
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 1200);
  };

  const renderStationField = (field) => {
    const isActive = activeField === field;
    const hasError = !!errors[field];
    const labelText = field === "from" ? "Leaving From" : "Going To";
    const placeholderText =
      field === "from" ? "City or Station" : "City or Station";

    return (
      <div className="relative w-full">
        <div
          className={`relative flex h-16 lg:h-[68px] w-full flex-col justify-center rounded-xl border bg-white px-4 transition-all duration-300 hover:border-slate-300 ${
            isActive
              ? "border-indigo-500 shadow-[0_0_0_4px_rgba(99,102,241,0.1)]"
              : hasError
                ? "border-rose-300"
                : "border-slate-200"
          }`}
        >
          <label
            className={`text-[10px] sm:text-[11px] font-bold uppercase tracking-wider mb-0.5 transition-colors ${isActive ? "text-indigo-600" : "text-slate-500"}`}
          >
            {labelText}
          </label>
          <div className="flex items-center gap-2">
            <MapPin
              className={`h-4 w-4 shrink-0 ${isActive ? "text-indigo-500" : "text-slate-400"}`}
            />
            <input
              type="text"
              value={draft[field]}
              onChange={(e) => {
                setDraft((curr) => ({ ...curr, [field]: e.target.value }));
                setSearch((curr) => ({ ...curr, [field]: null }));
                setActiveField(field);
                setErrors((curr) => ({ ...curr, [field]: "" }));
              }}
              onFocus={() => setActiveField(field)}
              placeholder={placeholderText}
              className="w-full bg-transparent text-sm sm:text-base font-semibold text-slate-900 outline-none placeholder:font-medium placeholder:text-slate-300"
            />
            {draft[field] && (
              <button
                type="button"
                onClick={() => clearStation(field)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
        <FieldError>{errors[field]}</FieldError>

        {isActive && (
          <div className="absolute left-0 right-0 top-[100%] z-50 mt-2 w-full origin-top rounded-2xl border border-slate-100 bg-white shadow-2xl">
            <div className="bg-slate-50 px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-500 rounded-t-2xl">
              {stationLoading ? "Searching networks..." : "Suggested Stations"}
            </div>
            <div className="max-h-[250px] sm:max-h-[300px] overflow-y-auto p-2">
              {stationLoading ? (
                [1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="my-1 h-12 sm:h-14 animate-pulse rounded-xl bg-slate-100"
                  />
                ))
              ) : stationResults.length > 0 ? (
                stationResults.map((station) => (
                  <button
                    key={station.code}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => selectStation(field, station)}
                    className="flex w-full items-center gap-3 sm:gap-4 rounded-xl px-3 py-2 sm:py-3 text-left transition-colors hover:bg-slate-50"
                  >
                    <div className="flex h-8 w-8 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-indigo-600">
                      <Train className="h-4 w-4 sm:h-5 sm:w-5" />
                    </div>
                    <div className="flex-1 overflow-hidden">
                      <p className="truncate text-sm sm:text-base font-bold text-slate-900">
                        {station.city}
                      </p>
                      <p className="truncate text-xs sm:text-sm text-slate-500">
                        {station.station}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="rounded bg-slate-100 px-2 py-1 text-[10px] sm:text-xs font-bold text-slate-600">
                        {station.code}
                      </span>
                    </div>
                  </button>
                ))
              ) : (
                <p className="p-4 text-center text-sm font-medium text-slate-500">
                  No stations found.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 selection:bg-indigo-100 selection:text-indigo-900">
      {/* Hero Section */}
      <section className="relative flex min-h-[450px] sm:min-h-[600px] flex-col items-center justify-start overflow-hidden bg-slate-950 pb-20 pt-16 sm:pb-32 sm:pt-24 lg:pt-32">
        <div className="absolute inset-0 z-0">
          <img
            src={Hero}
            alt="Scenic Train Journey"
            className="h-full w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/90 via-slate-950/60 to-slate-950/90" />
        </div>

        <div className="relative z-10 w-full max-w-7xl px-4 text-center sm:px-6 lg:px-8">
          <div className="mx-auto mb-4 sm:mb-6 flex max-w-fit items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 sm:px-4 sm:py-1.5 text-xs sm:text-sm font-medium text-indigo-100 backdrop-blur-md">
            <Award className="h-3 w-3 sm:h-4 sm:w-4 text-indigo-300" />
            <span>Voted #1 Premium Rail Booking Platform</span>
          </div>
          <h1 className="mx-auto max-w-4xl text-4xl font-extrabold tracking-tight text-white sm:text-5xl md:text-6xl lg:text-7xl">
            Redefining your <br className="hidden sm:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-300 to-emerald-300">
              journey on the rails.
            </span>
          </h1>
          <p className="mx-auto mt-4 sm:mt-6 max-w-2xl text-sm sm:text-base md:text-lg text-slate-300 px-2">
            Experience seamless booking, exclusive access to premium cabins, and
            intelligent routing across North America and Europe.
          </p>
        </div>
      </section>

      {/* Confirmation State (Simulated) */}
      {confirmation && (
        <section className="relative z-30 mx-auto -mt-10 mb-8 max-w-4xl px-4 sm:px-6">
          <div className="overflow-hidden rounded-2xl border border-emerald-200 bg-emerald-50 shadow-lg">
            <div className="flex items-center gap-3 sm:gap-4 border-b border-emerald-100 px-4 py-3 sm:px-6 sm:py-4">
              <div className="rounded-full bg-emerald-100 p-1.5 sm:p-2">
                <CheckCircle2 className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-bold text-emerald-950">
                  Search Successful
                </h3>
                <p className="text-xs sm:text-sm font-medium text-emerald-700">
                  Displaying optimal routes for your criteria.
                </p>
              </div>
            </div>
            <div className="px-4 py-3 sm:px-6 sm:py-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <span className="text-lg sm:text-xl font-extrabold text-slate-900">
                  {confirmation.route}
                </span>
                <span className="w-fit rounded-full bg-white px-3 py-1 text-xs sm:text-sm font-bold text-slate-600 shadow-sm border border-emerald-100">
                  {confirmation.details}
                </span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Booking Widget Container - Added enhanced prominent shadow */}
      <section className="relative z-20 mx-auto -mt-20 sm:-mt-24 lg:-mt-32 w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div
          ref={widgetRef}
          className="rounded-2xl sm:rounded-3xl border border-slate-100 bg-white p-5 sm:p-6 lg:p-8 shadow-[0_12px_40px_rgba(0,0,0,0.12)] sm:shadow-2xl sm:shadow-slate-300/60 backdrop-blur-xl"
        >
          {/* Top Controls: segmented control + Class dropdown matching reference */}
          <div className="mb-4 sm:mb-6 flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-slate-100 pb-4 sm:pb-6">
            {/* Trip Type Toggle */}
            <div className="flex w-full lg:w-auto rounded-xl bg-slate-50 p-1 border border-slate-100">
              {[
                { id: "one-way", label: "One Way" },
                { id: "round-trip", label: "Round Trip" },
              ].map((type) => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => {
                    setSearch({
                      ...search,
                      tripType: type.id,
                      returnDate:
                        type.id === "one-way" ? "" : search.returnDate,
                    });
                    setErrors({ ...errors, returnDate: "" });
                  }}
                  className={`flex-1 lg:flex-none rounded-lg px-4 sm:px-8 py-2.5 text-sm font-bold transition-all ${
                    search.tripType === type.id
                      ? "bg-white text-indigo-900 shadow-sm"
                      : "text-slate-500 hover:text-slate-900"
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>

            {/* Class Selection Dropdown */}
            <div className="flex w-full lg:w-auto items-center gap-3">
              <label className="text-sm font-bold text-slate-700 whitespace-nowrap">
                Class:
              </label>
              <div className="relative flex-1 lg:flex-none lg:w-64">
                <select
                  value={search.travelClass}
                  onChange={(e) =>
                    setSearch({ ...search, travelClass: e.target.value })
                  }
                  className="w-full cursor-pointer appearance-none rounded-xl border border-slate-200 bg-white py-2.5 pl-4 pr-10 text-sm font-bold text-slate-900 outline-none transition-all hover:border-slate-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                >
                  {travelClasses.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Form Grid */}
          <form onSubmit={handleSubmit}>
            <div className="flex flex-col gap-3 sm:gap-4 lg:flex-row lg:items-end lg:gap-2">
              {/* Destinations (From -> Swap -> To) */}
              <div className="relative flex flex-col gap-2 lg:w-[40%] lg:flex-row lg:gap-2">
                {renderStationField("from")}

                {/* Swap Button (Overlapping gap perfectly on mobile right edge) */}
                <div className="absolute right-4 top-1/2 z-10 -translate-y-1/2 lg:left-1/2 lg:right-auto lg:-translate-x-1/2">
                  <button
                    type="button"
                    onClick={handleSwap}
                    className="flex h-9 w-9 lg:h-10 lg:w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-indigo-600 shadow-sm transition-transform hover:scale-110 hover:border-indigo-300"
                  >
                    <ArrowRightLeft className="h-4 w-4 rotate-90 lg:rotate-0" />
                  </button>
                </div>

                {renderStationField("to")}
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-2 lg:w-[25%] lg:flex-row">
                <div
                  className={`relative flex h-16 lg:h-[68px] w-full flex-col justify-center rounded-xl border bg-white px-4 transition-all hover:border-slate-300 focus-within:border-indigo-500 focus-within:shadow-[0_0_0_4px_rgba(99,102,241,0.1)] ${errors.date ? "border-rose-300" : "border-slate-200"}`}
                >
                  <label className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-0.5">
                    Departure
                  </label>
                  <div className="flex items-center justify-between w-full relative">
                    <span className="text-sm sm:text-base font-semibold text-slate-900 pointer-events-none">
                      {formatShortDate(search.date) || "Select"}
                    </span>
                    <Calendar className="h-4 w-4 shrink-0 text-slate-900 pointer-events-none" />
                    <input
                      type="date"
                      min={minDate}
                      value={search.date}
                      onChange={(e) => {
                        setSearch({ ...search, date: e.target.value });
                        setErrors({ ...errors, date: "" });
                      }}
                      className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                    />
                  </div>
                  <FieldError>{errors.date}</FieldError>
                </div>

                <div
                  className={`relative flex h-16 lg:h-[68px] w-full flex-col justify-center rounded-xl border bg-white px-4 transition-all hover:border-slate-300 focus-within:border-indigo-500 focus-within:shadow-[0_0_0_4px_rgba(99,102,241,0.1)] ${errors.returnDate ? "border-rose-300" : "border-slate-200"}`}
                >
                  <label className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-0.5">
                    Return
                  </label>
                  {search.tripType === "round-trip" ? (
                    <div className="flex items-center justify-between w-full relative">
                      <span className="text-sm sm:text-base font-semibold text-slate-900 pointer-events-none">
                        {formatShortDate(search.returnDate) || "Select"}
                      </span>
                      <Calendar className="h-4 w-4 shrink-0 text-slate-900 pointer-events-none" />
                      <input
                        type="date"
                        min={search.date || minDate}
                        value={search.returnDate}
                        onChange={(e) => {
                          setSearch({ ...search, returnDate: e.target.value });
                          setErrors({ ...errors, returnDate: "" });
                        }}
                        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                      />
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() =>
                        setSearch({ ...search, tripType: "round-trip" })
                      }
                      className="text-left text-sm font-bold text-indigo-600 hover:text-indigo-800"
                    >
                      + Add Return
                    </button>
                  )}
                  <FieldError>{errors.returnDate}</FieldError>
                </div>
              </div>

              {/* Travelers */}
              <div ref={passengerRef} className="relative w-full lg:w-[20%]">
                <button
                  type="button"
                  onClick={() => setShowPassengerPanel(!showPassengerPanel)}
                  className="flex h-16 lg:h-[68px] w-full flex-col justify-center rounded-xl border border-slate-200 bg-white px-4 text-left transition-all hover:border-slate-300 focus:border-indigo-500 focus:shadow-[0_0_0_4px_rgba(99,102,241,0.1)]"
                >
                  <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-0.5">
                    Travelers
                  </span>
                  <div className="flex items-center justify-between">
                    <span className="text-sm sm:text-base font-semibold text-slate-900">
                      {passengerTotal} Guest{passengerTotal > 1 ? "s" : ""}
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 text-slate-400 transition-transform ${showPassengerPanel ? "rotate-180" : ""}`}
                    />
                  </div>
                </button>

                {showPassengerPanel && (
                  <div className="absolute right-0 top-[100%] z-50 mt-2 w-full min-w-[280px] rounded-2xl border border-slate-100 bg-white p-4 shadow-2xl">
                    {[
                      { id: "adults", label: "Adults", desc: "Age 13+" },
                      { id: "children", label: "Children", desc: "Age 2-12" },
                    ].map((group) => (
                      <div
                        key={group.id}
                        className="flex items-center justify-between py-3"
                      >
                        <div>
                          <p className="text-sm sm:text-base font-bold text-slate-900">
                            {group.label}
                          </p>
                          <p className="text-[10px] sm:text-xs font-medium text-slate-500">
                            {group.desc}
                          </p>
                        </div>
                        <div className="flex items-center gap-3 rounded-lg bg-slate-50 p-1">
                          <button
                            type="button"
                            onClick={() => updatePassengerGroup(group.id, -1)}
                            disabled={
                              group.id === "adults"
                                ? search.adults <= 1
                                : search.children <= 0
                            }
                            className="flex h-8 w-8 items-center justify-center rounded-md bg-white text-slate-600 shadow-sm disabled:opacity-50"
                          >
                            <Minus className="h-4 w-4" />
                          </button>
                          <span className="w-5 text-center text-sm sm:text-base font-bold text-slate-900">
                            {search[group.id]}
                          </span>
                          <button
                            type="button"
                            onClick={() => updatePassengerGroup(group.id, 1)}
                            className="flex h-8 w-8 items-center justify-center rounded-md bg-white text-slate-600 shadow-sm"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit Button (Pill shaped, full width on mobile) */}
              <div className="mt-2 lg:mt-0 lg:w-[15%]">
                <button
                  type="submit"
                  disabled={isSearching}
                  className="flex w-full items-center justify-center rounded-full lg:rounded-2xl bg-indigo-600 py-3.5 lg:h-[68px] text-base sm:text-lg font-bold text-white shadow-md transition-all hover:bg-indigo-700 active:scale-[0.98] disabled:opacity-80"
                >
                  {isSearching ? (
                    <div className="h-5 w-5 sm:h-6 sm:w-6 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  ) : (
                    <>
                      <Search className="mr-2 h-5 w-5" />
                      Search
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </section>

      <section>
        <PopularRoutes />
      </section>

      <section>
        <PopularBrandsPage />
      </section>

      {/* Features Section */}
      {/* <section className="mx-auto mt-16 sm:mt-24 max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-6 sm:gap-8 md:grid-cols-3">
          {features.map((feature, idx) => {
            const Icon = feature.icon;
            return (
              <div
                key={idx}
                className="group rounded-2xl border border-slate-100 bg-white p-6 sm:p-8 shadow-sm transition-all hover:shadow-xl hover:shadow-indigo-900/5"
              >
                <div className="mb-4 sm:mb-6 inline-flex rounded-2xl bg-indigo-50 p-3 sm:p-4 text-indigo-600 transition-transform group-hover:-translate-y-1 group-hover:scale-110">
                  <Icon className="h-6 w-6 sm:h-7 sm:w-7" />
                </div>
                <h3 className="mb-2 sm:mb-3 text-lg sm:text-xl font-bold text-slate-900">
                  {feature.title}
                </h3>
                <p className="text-sm sm:text-base text-slate-500 leading-relaxed">
                  {feature.text}
                </p>
              </div>
            );
          })}
        </div>
      </section> */}

      {/* Popular Routes */}
      {/* <section className="mx-auto mt-16 sm:mt-24 max-w-7xl px-4 pb-16 sm:pb-24 sm:px-6 lg:px-8">
        <div className="mb-8 sm:mb-10 flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <div>
            <span className="mb-2 block text-xs sm:text-sm font-bold uppercase tracking-widest text-indigo-600">
              Curated Journeys
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900">
              Popular Destinations
            </h2>
          </div>
          <button className="text-xs sm:text-sm font-bold text-indigo-600 hover:text-indigo-800 text-left md:text-right">
            View All Routes &rarr;
          </button>
        </div>

        <div className="grid gap-5 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {popularRoutes.map((route, idx) => (
            <div
              key={idx}
              className="group relative overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm transition-all hover:shadow-xl hover:shadow-slate-900/10"
            >
              <div className="aspect-[4/3] w-full overflow-hidden bg-slate-200">
                <img
                  src={route.image}
                  alt={route.to}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/20 to-transparent" />

              <div className="absolute bottom-0 w-full p-5 sm:p-6 text-white">
                <div className="mb-1 sm:mb-2 flex items-center gap-2 opacity-90">
                  <span className="text-xs sm:text-sm font-medium">
                    {route.from}
                  </span>
                  <ArrowRightLeft className="h-3 w-3" />
                  <span className="text-xs sm:text-sm font-medium">
                    {route.to}
                  </span>
                </div>
                <div className="flex items-end justify-between">
                  <h3 className="text-xl sm:text-2xl font-bold">{route.to}</h3>
                  <div className="text-right">
                    <span className="block text-[10px] sm:text-xs font-medium text-slate-300">
                      Starting at
                    </span>
                    <span className="text-base sm:text-lg font-bold text-emerald-400">
                      {route.fare}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section> */}

      {/* Trusted Partners */}
      {/* <section className="border-t border-slate-200 bg-slate-50 py-12 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
          <p className="mb-6 sm:mb-8 text-xs sm:text-sm font-bold uppercase tracking-widest text-slate-400">
            Trusted Partner Networks
          </p>
          <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-8 md:gap-16">
            {partnerBrands.map((brand) => (
              <span
                key={brand}
                className="text-lg sm:text-xl font-black tracking-tight text-slate-300 grayscale transition-all hover:text-slate-900 hover:grayscale-0"
              >
                {brand}
              </span>
            ))}
          </div>
        </div>
      </section> */}
    </div>
  );
}
